/* ─────────────────────────────────────────────────────────────
   Harmony generation.

   Pure functions — no DOM, no React — so the geometry can be
   verified offline the way colorMath is. Everything works in HSL
   because every rule is a rotation about the hue circle.

   Knows nothing about themes. It turns one seed into a set; what
   the set is for is the caller's business.
─────────────────────────────────────────────────────────────── */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
const clamp01 = n => clamp(n, 0, 1);

/** Hue normalised into [0, 360). */
export const normHue = h => ((h % 360) + 360) % 360;

/** Rotate a hue by a signed number of degrees. */
export const rotateHue = (h, deg) => normHue(h + deg);

/* A theme turns out to be about three real colour decisions (the
   --color-* audit on App.css: every theme overrides the same 22
   tokens, but those collapse to a hue, a surface and a ramp), so
   the useful range is small. Adobe's fixed five is more than the
   job needs. */
export const MIN_COLORS = 3;
export const MAX_COLORS = 5;
export const DEFAULT_COLORS = 3;

/* How far each extra pass moves lightness when a rule has fewer
   base hues than the caller asked for. */
const LIGHT_STEP = 0.12;
const L_FLOOR = 0.08;
const L_CEIL = 0.92;

/* Monochromatic and shades get a tighter band than the hue rules. They
   are a ramp of one colour, so a member at 8% lightness reads as black
   rather than as a shade of anything. */
const SHADE_FLOOR = 0.20;
const SHADE_CEIL = 0.86;
const SHADE_STEP = 0.15;

/**
 * The generating rules, as hue offsets from the seed.
 * `vary` marks the two that hold hue still and move through
 * lightness (and, for monochromatic, saturation) instead.
 *
 * "Custom" is deliberately absent: it is not a generated set but
 * the release of the constraint, so the caller keeps its current
 * colours and simply stops calling generateHarmony.
 */
export const HARMONY_RULES = [
  /* Seed first in every rule — the wheel rings handle 0 as the base
     and reseedFrom inverts against the offset at that index, so the
     invariant has to hold. Analogous is therefore seed-then-flanks
     rather than the textbook's left-to-right -30, 0, +30; same three
     hues, different order in the strip. */
  { id: 'analogous',     label: 'Analogous',           offsets: [0, -30, 30] },
  { id: 'complementary', label: 'Complementary',       offsets: [0, 180] },
  { id: 'split',         label: 'Split complementary', offsets: [0, 150, 210] },
  { id: 'triad',         label: 'Triad',               offsets: [0, 120, 240] },
  { id: 'square',        label: 'Square',              offsets: [0, 90, 180, 270] },
  { id: 'compound',      label: 'Compound',            offsets: [0, 30, 180, 210] },
  { id: 'monochromatic', label: 'Monochromatic',       offsets: [0], vary: 'sl' },
  { id: 'shades',        label: 'Shades',              offsets: [0], vary: 'l' },
];

export const CUSTOM_RULE = { id: 'custom', label: 'Custom' };

export const findRule = ruleId => HARMONY_RULES.find(r => r.id === ruleId) || null;

/* Extra passes alternate lighter then darker, widening each time:
   pass 1 +1 step, pass 2 -1, pass 3 +2, pass 4 -2. */
const passDelta = pass => (pass === 0
  ? 0
  : (pass % 2 === 1 ? 1 : -1) * Math.ceil(pass / 2) * LIGHT_STEP);

/**
 * Turn one seed into `count` colours under a rule.
 *
 * The first `offsets.length` entries are always the textbook rule,
 * in order, with the seed first. Only when more are asked for than
 * the rule defines does it extend, by stepping lightness across the
 * same hues — which is the one place the output is not the rule as
 * a colour theory textbook would state it.
 *
 * @param {{h:number,s:number,l:number}} seed
 * @param {string} ruleId
 * @param {number} count clamped to MIN_COLORS..MAX_COLORS
 * @returns {{h:number,s:number,l:number}[]}
 */
export function generateHarmony(seed, ruleId, count = DEFAULT_COLORS) {
  const rule = findRule(ruleId) || HARMONY_RULES[0];
  const n = Math.round(clamp(count, MIN_COLORS, MAX_COLORS));
  const s0 = clamp01(seed.s);
  const l0 = clamp01(seed.l);

  if (rule.vary) {
    /* One hue, spread through lightness — built OUTWARD FROM THE SEED.
       An earlier version ramped a fixed 0.92 down to 0.08 regardless of
       the seed, so every monochromatic or shades set ended on a
       near-black member and the colour you actually picked was not in
       it. Steps go to whichever side has more room, which keeps a dark
       seed off the floor and a light one off the ceiling, and the band
       is tighter than the hue rules' because a near-black shade reads
       as a mistake rather than as part of the set. */
    /* Pull a seed that sits outside the band to its nearest edge FIRST.
       Clamping afterwards instead collided with the step that had
       already landed on that edge, producing a duplicate member. */
    const start = clamp(l0, SHADE_FLOOR, SHADE_CEIL);
    const levels = [start];
    let up = start;
    let down = start;
    while (levels.length < n) {
      const roomUp = SHADE_CEIL - up;
      const roomDown = down - SHADE_FLOOR;
      if (roomUp < SHADE_STEP && roomDown < SHADE_STEP) break;
      if (roomUp >= roomDown && roomUp >= SHADE_STEP) {
        up += SHADE_STEP;
        levels.push(up);
      } else {
        down -= SHADE_STEP;
        levels.push(down);
      }
    }
    /* Only if the seed sits somewhere with no room either way — fall
       back to an even spread so the caller still gets n colours. */
    while (levels.length < n) {
      const t = levels.length / Math.max(1, n - 1);
      levels.push(clamp(SHADE_CEIL - t * (SHADE_CEIL - SHADE_FLOOR), SHADE_FLOOR, SHADE_CEIL));
    }

    /* Lightest first, so the strip reads as a ramp. */
    levels.sort((a, b) => b - a);
    return levels.map((l, i) => {
      const t = n === 1 ? 0 : i / (n - 1);
      const s = rule.vary === 'sl' ? clamp01(s0 * (0.55 + 0.45 * (1 - t))) : s0;
      return { h: normHue(seed.h), s, l: clamp(l, SHADE_FLOOR, SHADE_CEIL) };
    });
  }

  const offsets = rule.offsets;
  return Array.from({ length: n }, (_, i) => {
    const offset = offsets[i % offsets.length];
    const pass = Math.floor(i / offsets.length);
    return {
      h: rotateHue(seed.h, offset),
      s: s0,
      l: clamp(l0 + passDelta(pass), L_FLOOR, L_CEIL),
    };
  });
}

/**
 * The seed that would put `moved` at position `index` under a rule.
 *
 * This is what makes the wheel behave as a rigid body: drag any
 * handle, derive the seed it implies, regenerate the whole set from
 * that. The geometry is preserved by construction, so the set can
 * never be dragged into a state the rule does not describe.
 */
export function reseedFrom(index, moved, ruleId) {
  const rule = findRule(ruleId);
  if (!rule || rule.vary) return { h: normHue(moved.h), s: clamp01(moved.s), l: clamp01(moved.l) };
  const offset = rule.offsets[index % rule.offsets.length];
  return { h: rotateHue(moved.h, -offset), s: clamp01(moved.s), l: clamp01(moved.l) };
}

/**
 * Apply a known colour to one member of a set, keeping the rule.
 *
 * Pure so the component and its tests run the same code — this used to
 * live in the modal with a copy in the test, and the copy went stale.
 *
 * Monochromatic and shades ARE a lightness ramp derived from a single
 * colour, so they take the regenerated set whole; carrying the previous
 * lightnesses across would undo the regeneration and the picked colour
 * would never appear. Hue rules instead keep each tile's own lightness,
 * and the picked tile is taken verbatim — beyond a rule's base hues the
 * extra colours are defined BY a lightness step, so regeneration alone
 * would give that tile the right hue at the wrong lightness.
 */
export function applyPickedTo(colors, index, picked, ruleId) {
  if (!picked || !colors.length) return colors;
  const rule = findRule(ruleId);
  if (!rule) return colors.map((c, i) => (i === index ? { ...picked } : c));
  const base = reseedFrom(index, picked, ruleId);
  const next = generateHarmony(base, ruleId, colors.length);
  if (rule.vary) return next;
  return next.map((c, i) => (i === index
    ? { ...picked }
    : { ...c, l: colors[i] ? colors[i].l : c.l }));
}

/** Polar position of a colour on the wheel: hue is the angle, saturation the radius. */
export const hslToPolar = ({ h, s }) => ({ angle: normHue(h), radius: clamp01(s) });

/**
 * Wheel coordinates back to hue and saturation. `x` and `y` are
 * offsets from the wheel's centre in the same units as `radius`.
 */
export function polarToHsl(x, y, radius) {
  const dist = Math.hypot(x, y);
  return {
    h: normHue((Math.atan2(y, x) * 180) / Math.PI),
    s: clamp01(radius === 0 ? 0 : dist / radius),
  };
}
